/**
 * <s-product-gallery> — PDP main image + thumbnails.
 *
 * The track is a native scroll-snap row (swipe on touch, one large image at a
 * time). This component only keeps the thumbnails, arrows and track in sync.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap container (position: relative).
 *   [data-gallery-slide]          — one per media item, direct children of the track.
 *   [data-gallery-thumb="<i>"]    — thumbnail buttons; aria-current marks the visible slide.
 *   [data-gallery-thumbs]         — thumbnail list (position: relative), scrolled to keep the current thumb visible.
 *   [data-gallery-prev] / [data-gallery-next] — optional arrow buttons.
 *   data-variant-media="<variantId>:<mediaId> ..." on the host + [data-media-id] on slides — optional: choosing a variant in any
 *     s-variant-picker scrolls to that variant's image (after any device filtering). Variants without an image leave the gallery as is.
 *   [data-device-filter="kobo|kindle"] on the host — optional: slides/thumb items with data-device are shown only for the
 *     selected device, re-filtered when an s-variant-picker option containing "Kobo" is chosen or left. Hidden items are skipped.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });
    if (this.dataset.deviceFilter) {
      document.addEventListener('change', this.#handleVariantChange, { signal: this.#ac.signal });
    }
    if (this.dataset.variantMedia) {
      document.addEventListener('change', this.#handleVariantMedia, { signal: this.#ac.signal });
    }

    const track = this.#track;
    if (!track) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, { root: track, threshold: 0.6 });
    for (const slide of this.#slides) this.#observer.observe(slide);

    // Deep links already select a model on the server; align its image without an entrance animation.
    const initialMediaId = this.dataset.initialMediaId;
    const initialIndex = this.#slides.findIndex((slide) => slide.dataset.mediaId === initialMediaId);
    if (initialIndex !== -1) {
      track.scrollTo({ left: this.#slides[initialIndex].offsetLeft, behavior: 'instant' });
      this.#setCurrent(initialIndex);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-gallery-slide]')).filter((slide) => !slide.hidden);
  }

  get #thumbs() {
    return Array.from(this.querySelectorAll('[data-gallery-thumb]')).filter((thumb) => !thumb.closest('li')?.hidden);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb && this.contains(thumb)) {
      this.#goTo(this.#thumbs.indexOf(thumb));
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) {
      this.#goTo(this.#index - 1);
    } else if (event.target.closest('[data-gallery-next]')) {
      this.#goTo(this.#index + 1);
    }
  };

  #handleVariantChange = (event) => {
    if (!(event.target instanceof Element)) return;
    const picker = event.target.closest('s-variant-picker');
    if (!picker) return;
    const values = Array.from(picker.querySelectorAll('input:checked, select')).map((field) => field.value);
    const device = values.some((value) => value.includes('Kobo')) ? 'kobo' : 'kindle';
    if (device === this.dataset.deviceFilter) return;
    this.dataset.deviceFilter = device;
    for (const item of this.querySelectorAll('[data-device]')) {
      item.hidden = item.dataset.device !== device;
    }
    this.#track?.scrollTo({ left: 0 });
    this.#setCurrent(0);
  };

  #handleVariantMedia = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.closest('s-variant-picker')) return;
    const variantId = input instanceof HTMLSelectElement ? input.selectedOptions[0]?.dataset.variantId : input.dataset.variantId;
    if (!variantId) return;
    const pair = (this.dataset.variantMedia || '').split(' ').find((entry) => entry.startsWith(`${variantId}:`));
    if (!pair) return;
    const mediaId = pair.slice(variantId.length + 1);
    const index = this.#slides.findIndex((slide) => slide instanceof HTMLElement && slide.dataset.mediaId === mediaId);
    if (index !== -1) this.#goTo(index);
  };

  #handleIntersect = (entries) => {
    const slides = this.#slides;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = slides.indexOf(entry.target);
      if (index !== -1) this.#setCurrent(index);
    }
  };

  #goTo(index) {
    const slides = this.#slides;
    const track = this.#track;
    if (!track || slides.length === 0) return;

    const next = (index + slides.length) % slides.length;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slides[next].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setCurrent(next);
  }

  #setCurrent(index) {
    this.#index = index;
    const thumbs = this.#thumbs;
    thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    this.#revealThumb(thumbs[index]);
  }

  // Scroll only the thumbnail list (never the page) so the current thumb stays visible.
  #revealThumb(thumb) {
    const list = this.querySelector('[data-gallery-thumbs]');
    const item = thumb?.closest('li');
    if (!list || !item) return;

    const vertical = list.scrollHeight > list.clientHeight + 1;
    if (vertical) {
      const top = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
      list.scrollTo({ top, behavior: 'smooth' });
    } else {
      const left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
      list.scrollTo({ left, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
